/**
 * Custom React hook for school search functionality
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { searchSchools } from 'src/generated/api';
/**
 * Custom hook for school search with debouncing
 * @param {number} debounceMs - Debounce delay in milliseconds
 * @returns {UseSchoolSearchResult} Search state and methods
 */
export const useSchoolSearch = (debounceMs = 300) => {
    const [schools, setSchools] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [totalResults, setTotalResults] = useState(0);
    const debounceTimerRef = useRef(null);
    /**
     * Clear any pending debounce timers on unmount
     */
    useEffect(() => () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
    }, []);
    /**
     * Search for schools by ZIP code with debouncing
     * @param {string} zipCode - ZIP code to search
     */
    const searchSchoolsFn = useCallback(async (zipCode) => {
        // Clear previous timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        // Validate ZIP code format (3-5 digits)
        if (!zipCode || !/^\d{3,5}$/.test(zipCode)) {
            setSchools([]);
            setError(null);
            setHasMore(false);
            setTotalResults(0);
            return;
        }
        setLoading(true);
        setError(null);
        // Set new debounce timer
        const timer = setTimeout(async () => {
            try {
                const params = {
                    zipCode,
                    limit: 20,
                };
                const response = await searchSchools(params);
                const result = response.data;
                // Pad zip codes to 5 digits for display
                const schoolsWithPaddedZips = (result.data || []).map((school) => {
                    if (school.address?.zipCode) {
                        return {
                            ...school,
                            address: {
                                ...school.address,
                                zipCode: school.address.zipCode.padStart(5, '0'),
                            },
                        };
                    }
                    return school;
                });
                setSchools(schoolsWithPaddedZips);
                // Use cursor to determine if there are more results (if cursor is present)
                setHasMore(!!result.meta?.cursor);
                // We only get result count for current page, using it for now
                setTotalResults(result.meta?.resultCount || 0);
                setError(null);
            }
            catch (err) {
                setSchools([]);
                setError(err instanceof Error
                    ? err.message
                    : 'Failed to search schools. Please try again.');
                setHasMore(false);
                setTotalResults(0);
            }
            finally {
                setLoading(false);
            }
        }, debounceMs);
        debounceTimerRef.current = timer;
    }, [debounceMs]);
    /**
     * Clear search results and errors
     */
    const clearResults = useCallback(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        setSchools([]);
        setError(null);
        setLoading(false);
        setHasMore(false);
        setTotalResults(0);
    }, []);
    return {
        schools,
        loading,
        error,
        hasMore,
        totalResults,
        searchSchools: searchSchoolsFn,
        clearResults,
    };
};
export default useSchoolSearch;
